import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API_URL =
  import.meta.env.VITE_API_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

export default function FAQSection() {
  const { t, translations, currentLanguage } = useLanguage();
  const [items, setItems] = useState([]);
  const [openIdx, setOpenIdx] = useState(0);

  // Load from API; fall back to local i18n list when the call fails
  // (e.g. development without backend, or first deploy before migration).
  useEffect(() => {
    let cancelled = false;
    const fallback = Array.isArray(translations?.faq?.items)
      ? translations.faq.items.map((it, idx) => ({
          id: `local-${idx}`,
          question: it.q,
          answer: it.a,
        }))
      : [];

    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/faq`, {
          params: { lang: currentLanguage || 'en' },
          timeout: 6000,
        });
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        if (cancelled) return;
        if (list.length > 0) {
          setItems(list);
          setOpenIdx(0);
        } else {
          setItems(fallback);
        }
      } catch {
        if (!cancelled) setItems(fallback);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentLanguage, translations]);

  if (items.length === 0) return null;

  return (
    <section id="faq" className="section-padding section-tone-b">
      <div className="container-premium">
        <div className="section-head">
          <span className="section-eyebrow">{t('faq.eyebrow', 'FAQ')}</span>
          <h2 className="section-title">{t('faq.title', 'Frequently Asked Questions')}</h2>
          <p className="section-sub">
            {t(
              'faq.subtitle',
              'Everything you need to know before your visit. Still have questions? Message us anytime.'
            )}
          </p>
        </div>

        <div className="faq-wrap">
          {items.map((item, idx) => {
            const open = idx === openIdx;
            return (
              <div key={item.id ?? idx} className={`faq-item ${open ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="faq-q"
                  aria-expanded={open}
                  onClick={() => setOpenIdx(open ? -1 : idx)}
                >
                  <span>{item.question}</span>
                  <ChevronDown size={18} className="faq-chev" />
                </button>
                <div className="faq-a-wrap">
                  <p className="faq-a">{item.answer}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
